// src/auditoria/components/ds/grade-canvas/GradeCanvas.tsx
//
// O casulo da grade — DESIGN-SYSTEM.md §4 e §4.1.
//
// Implementa, num arquivo só: `.card` na variante grade, `.tabs`/`.tab`/`.tab.on`
// (58 artboards), a faixa de toolbar com `.search` (46) e `.filtpill` (36),
// `.thead` (105 artboards — via `SX_GRADE_CANVAS`), `.trow`/`.trow.last` (75),
// `.pager` (44) e `.pgbtn` (44). `.cbx` **não é renderizada**: checkbox em grade
// é proibido pela regra B do canvas.
//
// No canvas isso não é "abas + toolbar + tabela + pager" soltos; é **um `.card`
// com quatro filhos**, nesta ordem, sem padding próprio. O erro estrutural que o
// app comete hoje — `<Tabs sx={{ borderBottom:1, mb:2 }}>` FORA do `Paper`
// (legislacao, tipos_legislacao, fundamentacao-legal) — produz duas superfícies
// onde o canvas tem uma. Por isso o kit entrega um casulo: a ordem errada passa
// a ser irrepresentável.
//
// Os quatro nomes que a spec lista como arquivos irmãos (`SX_GRADE_CANVAS.ts`,
// `GradeTabs.tsx`, `GradeToolbar.tsx`, `GradePager.tsx`) vivem aqui dentro, por
// limite de escopo do lote; a assinatura é a da spec, letra por letra.
//
// ----------------------------------------------------------------------
// RODADA DE CONSOLIDAÇÃO DA CAMADA COMPARTILHADA
//
//  1. DECISOES-PENDENTES.md §3.6 — **6 lotes** (L01 F4, L02, L04, L12, L14, L24 K1):
//     o casulo não tinha slot de título. 105 artboards têm `.thead` e boa parte tem
//     um título 14px/700 com `padding:16px 16px 0` DENTRO do mesmo card, acima da
//     tabela. Cinco lotes hospedaram esse título no slot `tabs` — "um slot com nome
//     errado", nas palavras de L24 — e dois escreveram componente local
//     (`DsPanelCard` + `mx:'-16px'`, `CabecalhoGrade`). Agora há `title` /
//     `titleAction` / `header`, entre as abas e a toolbar, que é onde o canvas
//     desenha. Quando há título E toolbar, a toolbar recolhe o padding de topo de
//     16 para 12 (`GradeToolbar dense`), senão o vão soma 32px e não bate com
//     artboard nenhum.
//
//  2. DECISOES-PENDENTES.md §3.3 — **9 lotes** (L10 F4, L13 D2, L16 D3, L17 D3,
//     L18, L21, L22, L24 D3/K2, L25): a coluna de ações do canvas — 96px, dois
//     ícones de TRAÇO de 18px alinhados à direita em `text.secondary` — estava
//     reimplementada à mão em ~20 arquivos. Virou o helper `colunaAcoes`, no mesmo
//     espírito do `colunaLink` de `DsCellLink.tsx`: uma linha na lista de colunas.
//     Ele NÃO conserta o `formatColumns` do `tx-data-grid` (que continua montando
//     menu de três pontos com `solar:*-bold` quando a tela passa `onEdit`/`onDelete`
//     e nenhuma coluna manual) — isso é lote de infra, item (a) de §3.3.
//
//  3. RESPIRO LATERAL DA GRADE — pedido do dono do produto sobre a tela
//     `Cadastros Geográficos` (`/dashboard/cadastros/geograficos/uf`): "essa tabela
//     está colada na lateral e o buscar e o atualizar têm um padding lateral (…) é
//     pra fazer em todos". Não é gosto, é falta de fidelidade: no canvas o `.thead`
//     é `padding:10px 16px` e o `.trow` é `padding:0 16px`
//     (`auditoria_design_canvas/cadastros-geograficos.dc.html`; 110 theads e 462
//     trows em 16px, contra 24 em 12px), ou seja as colunas nascem 16px para dentro
//     da borda do card — a mesma medida que os três slots deste arquivo já pagam
//     (`GradeTabs px:'16px'`, `GradeToolbar '16px 16px 12px'`, `GradePager
//     '12px 16px'`). Só `SX_GRADE_CANVAS` não pagava, e por isso a tabela encostava
//     enquanto a busca e o botão Atualizar respiravam. Agora paga — ver o bloco
//     "respiro lateral" dentro do `sx`, que explica a técnica e por que ela não
//     rouba largura das colunas.

import type { SystemStyleObject } from '@mui/system';
import type { Theme, SxProps } from '@mui/material/styles';
import type { ReactNode, MouseEvent, SyntheticEvent } from 'react';
import type { GridColDef, GridValidRowModel, GridRenderCellParams } from '@mui/x-data-grid';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import ButtonBase from '@mui/material/ButtonBase';
import { paperClasses } from '@mui/material/Paper';

import { DsIcon } from '../DsIcon';
import {
  DS_TABULAR,
  DS_BOX_RADIUS,
  DS_ROW_HEIGHT,
  DS_HEAD_HEIGHT,
  DS_CELL_FONT_COMPACTA,
  DS_CELL_LINE_COMPACTA,
  DS_CELL_PAD_Y_COMPACTA,
  DS_ROW_HEIGHT_COMPACTA,
  DS_HEAD_HEIGHT_COMPACTA,
} from '../tokens';

import type { DsIconName } from '../DsIcon';

// ----------------------------------------------------------------------
// Grade: o que o tema não alcança porque `sx` vence `styleOverrides`.
// ----------------------------------------------------------------------

/**
 * Respiro lateral do card na variante grade, em px.
 *
 * É a medida do `.thead` (`padding:10px 16px`) e do `.trow` (`padding:0 16px`) do
 * canvas, a mesma que `GradeTabs`, `GradeToolbar` e `GradePager` já escrevem como
 * literal `16px`. Vive aqui, e não em `ds/tokens.ts`, por limite de escopo do lote.
 */
const DS_GRADE_RESPIRO = 16;

// ----------------------------------------------------------------------
// Pacote de props + `sx` do `.thead`/`.trow` do canvas, aplicável **isolado** ao
// `TxDataGrid` que a tela já tem (`<TxDataGrid {...SX_GRADE_CANVAS} />`) — é a
// adoção parcial legítima, sem o casulo.
//
// `rowHeight`, `columnHeaderHeight` e `density` são props, não `sx`.
//
// O `density` é OBRIGATÓRIO aqui, ao contrário do que §4.1 afirma ("`density` não
// é passada; `rowHeight` vence"). No `@mui/x-data-grid` 7.28.2 as duas alturas são
// MULTIPLICADAS pelo fator da densidade —
// `hooks/features/dimensions/useGridDimensions.js:278-279`
// (`Math.floor(validRowHeight * density)` e `Math.floor(columnHeaderHeight * density)`)
// com `COMPACT_DENSITY_FACTOR = 0.7` em `densitySelector.js:2`. Como o `TxDataGrid`
// crava `density: density ?? 'compact'`, sem o `'standard'` abaixo a grade sairia
// com 44*0.7 = 30px de linha e 38*0.7 = 26px de cabeçalho.
//
// Passado por aqui o valor chega ao lugar certo nos três modos do `TxDataGrid`:
// ele desestrutura `density` da própria prop, então `density ?? 'compact'` resolve
// para `'standard'` em `managed` e `server`, e em `client` o `propsUsuario` (que
// carrega o `density`) é espalhado por último.
//
// `--DataGrid-containerBackground: background.neutral` e `borderTopStyle:'dashed'`
// já existem no tema (`src/theme/core/components/mui-x-data-grid.tsx:102-104` e
// `:139`) e não são redefinidos aqui — o respiro lateral só LÊ a variável de fundo
// para reconstituir a faixa do `.thead` de ponta a ponta.
// ----------------------------------------------------------------------
export type DsDensidadeGrade = 'padrao' | 'compacta';

export type PacoteGradeCanvas = {
  rowHeight: number;
  columnHeaderHeight: number;
  density: 'standard';
  sx: SxProps<Theme>;
};

/**
 * Monta o pacote de props + `sx` da grade na densidade pedida.
 *
 * `padrao` é byte a byte o que `SX_GRADE_CANVAS` sempre entregou — as ~92 telas
 * dos dois apps que espalham a constante não enxergam diferença nenhuma.
 * `compacta` é opt-in e só muda altura, respiro e corpo do texto: **nenhuma
 * coluna some, nada é truncado e nada vai para trás de tooltip**.
 */
function pacoteGradeCanvas(densidade: DsDensidadeGrade): PacoteGradeCanvas {
  const compacta = densidade === 'compacta';

  return {
    rowHeight: compacta ? DS_ROW_HEIGHT_COMPACTA : DS_ROW_HEIGHT,
    columnHeaderHeight: compacta ? DS_HEAD_HEIGHT_COMPACTA : DS_HEAD_HEIGHT,
    density: 'standard', // fator 1: sem ele o MUI X multiplica as alturas por 0.7
    sx: {
      // ------------------------------------------------------------------
      // Respiro lateral — §3 do cabeçalho.
      //
      // O padding vai no `.MuiDataGrid-main` porque **é ele que o MUI X mede**:
      // `useGridVirtualScroller.js:98-110` observa o `main` com `ResizeObserver` e
      // publica `entry.contentRect.width`, que por definição já exclui o padding.
      // Esse valor vira `dimensions.viewportOuterSize.width`
      // (`useGridDimensions.js:153-156`), que é de onde saem (a) a largura
      // disponível para as colunas `flex` (`gridColumnsUtils.js:145`), (b) o teste
      // de rolagem horizontal (`useGridDimensions.js:161-168`) e (c) a largura do
      // overlay de vazio/carregando (`GridOverlays.js:71`). Resultado: os 32px são
      // descontados ANTES do cálculo, então as colunas `flex` encolhem sozinhas em
      // vez de transbordar — o respiro não cria rolagem horizontal nem rouba
      // largura útil. Padding no `.MuiDataGrid-virtualScroller` (ou nas linhas)
      // faria o contrário: a linha continua com `width:var(--DataGrid-rowWidth)`,
      // medida na largura cheia, e sobra 32px para fora.
      //
      // Casos que o recuo poderia estragar, e por que não estraga:
      //  - **coluna de ações** (`colunaAcoes`, última, 96px, ícones à direita):
      //    nada é acrescentado DENTRO da célula, então os 96px continuam inteiros e
      //    os ícones só se deslocam junto com a coluna, que passa a terminar a 16px
      //    da borda. Padding em `.MuiDataGrid-cell:last-of-type` empurraria os
      //    ícones para fora da célula — e `:last-of-type` nem é confiável com
      //    virtualização/coluna fixada.
      //  - **coluna de checkbox**: idem — largura fixa intocada, o quadrado segue
      //    centrado na coluna e o do cabeçalho continua no mesmo eixo do da linha.
      //  - **colunas fixadas**: ficam `position:sticky` dentro do
      //    `.MuiDataGrid-virtualScroller`, que agora começa 16px para dentro; a
      //    primeira visível cola no recuo, não na borda do card, sem depender de
      //    quem é a primeira no DOM.
      //  - **overlay de vazio/carregando**: o MUI dimensiona o
      //    `.MuiDataGrid-overlayWrapperInner` com a MESMA largura medida, e o recuo
      //    é simétrico — o centro do overlay continua sendo o centro do card.
      '& .MuiDataGrid-main': {
        paddingInline: `${DS_GRADE_RESPIRO}px`,
        // Fundo da faixa do `.thead`, RECOLHIDO nos mesmos 16px do conteúdo.
        //
        // O artboard desenha a faixa sangrando de ponta a ponta, e foi assim que
        // ela nasceu aqui. O dono do produto olhou na tela e pediu o contrário:
        // "ainda ficou a faixa da header sem o padding, e coloca uma borda nas
        // laterais". Vale a decisão dele — a faixa agora começa e termina no mesmo
        // eixo da busca, do título e das células. É divergência consciente do
        // artboard, não descuido.
        //
        // Fica atrás do cabeçalho (é o primeiro filho e o scroller é posicionado,
        // logo pinta depois), então não cobre texto nenhum.
        '&::before': {
          content: '""',
          position: 'absolute',
          insetInline: `${DS_GRADE_RESPIRO}px`,
          top: 0,
          height: 'var(--DataGrid-headersTotalHeight)',
          background: 'var(--DataGrid-containerBackground)',
          pointerEvents: 'none',
        },
        // A moldura do `.thead`: as duas réguas mais as laterais que o dono do
        // produto pediu, fechando a faixa num retângulo. Desenhada POR CIMA (o
        // `.MuiDataGrid-topContainer` é `z-index:40` e cria contexto de
        // empilhamento; 50 passa por ele e ainda fica abaixo das barras de rolagem,
        // que são 60). A altura é exatamente `--DataGrid-headersTotalHeight` com as
        // bordas por dentro (`border-box`): 10+21+10 = 41px incluindo as linhas.
        '&::after': {
          content: '""',
          position: 'absolute',
          insetInline: `${DS_GRADE_RESPIRO}px`,
          top: 0,
          height: 'var(--DataGrid-headersTotalHeight)',
          boxSizing: 'border-box',
          border: '1px solid var(--palette-divider)',
          pointerEvents: 'none',
          zIndex: 50,
        },
      },
      // As barras de rolagem do MUI X são `position:absolute` ancoradas no `main`
      // (`GridVirtualScrollbar.js:44-57`), ou seja na caixa de padding: sem isto a
      // vertical encostaria na borda do card e a horizontal sobraria 32px à direita.
      '& .MuiDataGrid-scrollbar--vertical': { right: `${DS_GRADE_RESPIRO}px` },
      '& .MuiDataGrid-scrollbar--horizontal': {
        left: `${DS_GRADE_RESPIRO}px`,
        width: `calc(100% - ${DS_GRADE_RESPIRO * 2}px)`,
      },
      // As réguas subiram para o `::after` do `main` acima, que é quem desenha a
      // moldura inteira (topo, base e laterais) no eixo dos 16px. Aqui precisa ser
      // `none` explícito, senão sairia uma segunda linha por baixo: o piso do tema
      // (`theme/core/components/mui-x-data-grid.tsx:117-120`) também as declara
      // neste seletor, e `sx` é quem vence.
      '& .MuiDataGrid-columnHeaders': {
        borderTop: 'none',
        borderBottom: 'none',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: 14,
        fontWeight: 600,
        color: 'text.secondary',
        textTransform: 'none',
      },
      // Conteúdo centrado na altura da linha. Sem isto, célula com `renderCell`
      // (o `DsCellLink`, que é `display:block`) encosta no topo enquanto as células
      // de texto puro ficam centradas — era o desalinhamento visível entre a coluna
      // Nome e as colunas ID/Sigla. O `minWidth:0` preserva o `text-overflow`, que
      // um contêiner flex quebraria.
      '& .MuiDataGrid-cell': {
        // 13 no padrão; 12 na compacta, que é o literal do `td` do artboard e o
        // piso de legibilidade combinado — não desce disso.
        fontSize: compacta ? DS_CELL_FONT_COMPACTA : 13,
        borderTopStyle: 'dashed',
        fontVariantNumeric: 'tabular-nums',
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        // Só na compacta: o respiro vertical vira explícito porque é ele, e não o
        // `rowHeight`, que manda quando a tela pede `getRowHeight={() => 'auto'}`
        // (célula que empilha conteúdo — o caso de `malha-fiscal-execucao`).
        //
        // Com `rowHeight` fixo isto não altera a altura da linha, só encolhe a
        // caixa de conteúdo: sobram DS_ROW_HEIGHT_COMPACTA - 12 = 22px, que
        // abrigam o `DsStatusChip` (21px) e o `DsCellLink` (13/1.5 = 19.5px). Uma
        // grade compacta com conteúdo de célula mais alto que isso precisa subir o
        // `rowHeight` junto.
        ...(compacta && {
          paddingBlock: `${DS_CELL_PAD_Y_COMPACTA}px`,
          lineHeight: DS_CELL_LINE_COMPACTA,
        }),
      },
      // `display:'none'` tiraria o separador do layout e, com ele, o gesto de
      // redimensionar coluna (o handler de `useGridColumnResize.js:363` faz hit-test
      // no próprio separador). O canvas não desenha a barrinha, mas remover função
      // é proibido pelo cabeçalho de §8: ela fica transparente e volta a aparecer
      // quando o ponteiro entra no cabeçalho.
      '& .MuiDataGrid-columnSeparator': { color: 'transparent' },
      '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator--resizable': {
        color: 'var(--DataGrid-rowBorderColor)',
      },
    },
  };
}

export const SX_GRADE_CANVAS: PacoteGradeCanvas = pacoteGradeCanvas('padrao');

/**
 * Mesmo pacote na densidade compacta — **opt-in**, ninguém o herda por engano.
 *
 * Linha 44 -> 34, cabeçalho 38 -> 32, corpo da célula 13 -> 12 com `line-height`
 * 1.35 e 6px de respiro vertical. O respiro LATERAL (`DS_GRADE_RESPIRO`) não
 * muda: ele é largura, não altura, e encolhê-lo não faz caber mais linha nenhuma.
 *
 * ```tsx
 * <GradeCanvas densidade="compacta" toolbar={…}>
 *   <TxDataGrid {...SX_GRADE_CANVAS_COMPACTA} … />
 * </GradeCanvas>
 * ```
 *
 * As duas metades andam juntas: a constante trata a grade, a prop `densidade` do
 * casulo trata a moldura em volta dela (toolbar e título). Passar só uma das duas
 * funciona, mas deixa a tabela densa dentro de um card folgado.
 *
 * Com `getRowHeight={() => 'auto'}` o `rowHeight` deixa de valer e vira papel da
 * tela repetir o piso: `'& .MuiDataGrid-row': { minHeight: '34px !important' }`,
 * de `DS_ROW_HEIGHT_COMPACTA`.
 */
export const SX_GRADE_CANVAS_COMPACTA: PacoteGradeCanvas = pacoteGradeCanvas('compacta');

// ----------------------------------------------------------------------
// Slot 1 — abas
// ----------------------------------------------------------------------

export type GradeTabsProps = {
  value: string;
  onChange: (v: string) => void;
  items: { value: string; label: ReactNode }[];
};

/**
 * `.tabs` — primeiro filho do card, nunca fora dele.
 *
 * Tipografia, altura (41px), gap de 4px, indicador e cores do ativo/inativo vêm
 * TODOS de `src/theme/core/components/tabs.tsx` — os mesmos que a malha fiscal
 * usa via `TabList` do `@mui/lab`, que É o `Tabs` do material (o `TabList` só
 * injeta o `value` do `TabContext`, sem estilo nenhum). Repetir o tema aqui foi
 * o que fez estas abas divergirem do modelo. Sobra só o que é da calha do card:
 * os 16px laterais e a linha de 1px do trilho.
 *
 * O `margin-bottom:-1px` que estava aqui NÃO desenhava o indicador: no MUI o
 * indicador é um `<span>` `position:absolute; bottom:0` irmão da lista, e o
 * scroller já termina em cima da borda da raiz. A margem só fazia o `Tab`
 * (40,43px) estourar em 1px o padding box da raiz (39,43px) — razão 0,975
 * contra o `threshold: 0.99` do IntersectionObserver do `Tabs` — e as DUAS
 * setas de rolagem acendiam permanentemente, mesmo com duas abas e a linha
 * inteira sobrando. Era transcrição errada do canvas, onde a borda da própria
 * `.tab` desenha o indicador.
 *
 * `scrollButtons={false}` desliga as setas de vez (pedido do dono do produto, e
 * nenhum dos 309 artboards desenha seta). `variant="scrollable"` fica explícito:
 * sem ele o overflow seria recortado e a aba viraria inalcançável; com ele o
 * trilho continua rolando por gesto, Shift+roda e teclado.
 */
export function GradeTabs({ value, onChange, items }: GradeTabsProps) {
  return (
    <Tabs
      value={value}
      onChange={(_evento: SyntheticEvent, novoValor: string) => onChange(novoValor)}
      variant="scrollable"
      scrollButtons={false}
      sx={{
        px: '16px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {items.map((item) => (
        <Tab key={item.value} value={item.value} label={item.label} />
      ))}
    </Tabs>
  );
}

// ----------------------------------------------------------------------
// Slot 2 — toolbar
// ----------------------------------------------------------------------

export type GradeToolbarProps = {
  /** `<DsSearchField …/>` — recebe `flex:1`. */
  search?: ReactNode;
  /** `<DsFilterPill …/>[]`. */
  filters?: ReactNode;
  /** `<DsButton …/>[]`. */
  actions?: ReactNode;
  /**
   * Recolhe o padding de topo de 16 para 12px. O `GradeCanvas` liga sozinho quando
   * há título acima da toolbar — o título já gasta 16px de respiro embaixo.
   */
  dense?: boolean;
  /**
   * `compacta` corta mais 4px em cima e embaixo da faixa. É o "espaço morto" entre
   * a busca e a primeira linha da grade: sozinho não cabe uma linha a mais, mas
   * soma com o cabeçalho e a linha encolhidos de `SX_GRADE_CANVAS_COMPACTA`.
   */
  densidade?: DsDensidadeGrade;
};

/**
 * Faixa de toolbar, ordem fixa: busca → pílulas → ações.
 *
 * **A busca estica.** Hoje o app fixa 300/320/220px em ~15 telas (cnae, servicos,
 * exportacao-arquivo, dte-protocolos, loteamento, indexadores) e deixa um vazio no
 * meio da faixa; `flex:1` é o que o canvas manda.
 */
export function GradeToolbar({
  search,
  filters,
  actions,
  dense = false,
  densidade = 'padrao',
}: GradeToolbarProps) {
  const compacta = densidade === 'compacta';

  let padding: string;

  if (compacta) {
    padding = dense ? '8px 16px' : '12px 16px 8px';
  } else {
    padding = dense ? '12px 16px' : '16px 16px 12px';
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding,
        flexWrap: 'wrap',
      }}
    >
      {search && <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>{search}</Box>}

      {filters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            flex: '0 0 auto',
          }}
        >
          {filters}
        </Box>
      )}

      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------
// Slot 4 — rodapé
// ----------------------------------------------------------------------

export type GradePagerProps = {
  de: number;
  ate: number;
  total: number;
  onPrev?: () => void;
  onNext?: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  /** Texto livre; em `visita-fiscal` é "4 contribuintes da equipe". */
  label?: ReactNode;
};

/** `.pgbtn` — quadrado de 28px com borda, não `IconButton` circular. */
const SX_PGBTN: SxProps<Theme> = {
  width: 28,
  height: 28,
  display: 'grid',
  placeItems: 'center',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${DS_BOX_RADIUS}px`,
  color: 'text.secondary',
  '&.Mui-disabled': { opacity: 0.4 },
};

/**
 * `.pager` — texto `1–6 de 18` em dígitos tabulares e **dois** botões quadrados.
 *
 * Nenhum dos 44 artboards com rodapé tem seletor de linhas por página: quando o
 * `TxDataGrid` fica dentro do casulo, passe `hideFooter` (ou ao menos
 * `hidePageSizeSelector`). `OPCOES_PAGINA_CLIENTE` permanece no estado, some só da UI.
 */
export function GradePager({
  de,
  ate,
  total,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  label,
}: GradePagerProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '12px',
        padding: '12px 16px',
        fontSize: 12,
        color: 'text.secondary',
      }}
    >
      <Box component="span" sx={DS_TABULAR}>
        {label ?? `${de}–${ate} de ${total}`}
      </Box>

      <ButtonBase
        aria-label="Página anterior"
        onClick={onPrev}
        disabled={prevDisabled ?? !onPrev}
        sx={SX_PGBTN}
      >
        <DsIcon name="chevron-esq" size={14} />
      </ButtonBase>

      <ButtonBase
        aria-label="Próxima página"
        onClick={onNext}
        disabled={nextDisabled ?? !onNext}
        sx={SX_PGBTN}
      >
        <DsIcon name="chevron-dir" size={14} />
      </ButtonBase>
    </Box>
  );
}

// ----------------------------------------------------------------------
// O casulo
// ----------------------------------------------------------------------

export type GradeCanvasProps = {
  tabs?: ReactNode;
  /**
   * Título do card, 14px/700 com `padding:16px 16px 0` — o `.thead` de §3.6. Vem
   * depois das abas e antes da toolbar, que é onde o canvas o desenha.
   */
  title?: ReactNode;
  /** Canto direito da faixa do título (link, contagem, `DsButton` pequeno). */
  titleAction?: ReactNode;
  /**
   * Cabeçalho livre, no lugar do `title`. Vai cru, sem embrulho e sem padding —
   * mesmo contrato do slot `tabs`. Use quando a faixa não for "título + ação".
   */
  header?: ReactNode;
  toolbar?: GradeToolbarProps;
  /** `<TxDataGrid …/>` ou o conteúdo da grade. */
  children: ReactNode;
  pager?: ReactNode;
  sx?: SxProps<Theme>;
  /**
   * Densidade da MOLDURA (título e toolbar). A grade lá dentro é tratada pelo
   * pacote que a tela espalha no `TxDataGrid` — use `SX_GRADE_CANVAS_COMPACTA`
   * junto, senão a tabela fica densa dentro de um card folgado.
   *
   * Padrão `'padrao'`: quem não passa nada não vê diferença nenhuma.
   */
  densidade?: DsDensidadeGrade;
};

export function GradeCanvas({
  tabs,
  title,
  titleAction,
  header,
  toolbar,
  children,
  pager,
  sx,
  densidade = 'padrao',
}: GradeCanvasProps) {
  const temCabecalho = title !== undefined || header !== undefined;
  const compacta = densidade === 'compacta';

  return (
    <Card
      sx={[
        // Raio 16 e `customShadows.card` já vêm do tema
        // (`src/theme/core/components/card.tsx`), sem borda.
        { p: 0, overflow: 'hidden' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {tabs}

      {header}

      {title !== undefined && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            // §3.6 mede `16px 16px 0` porque nos artboards vem sempre uma faixa
            // logo abaixo. Sem toolbar, o título encostaria no `.thead` — aí ele
            // paga o mesmo 12px de respiro que a toolbar paga.
            padding: (() => {
              if (compacta) {
                return toolbar ? '12px 16px 0' : '12px 16px 8px';
              }

              return toolbar ? '16px 16px 0' : '16px 16px 12px';
            })(),
          }}
        >
          <Box component="h2" sx={{ fontSize: 14, fontWeight: 700, m: 0, minWidth: 0 }}>
            {title}
          </Box>

          {titleAction && <Box sx={{ flex: '0 0 auto' }}>{titleAction}</Box>}
        </Box>
      )}

      {toolbar && <GradeToolbar dense={temCabecalho} densidade={densidade} {...toolbar} />}

      {/*
        O `TxDataGrid` se embrulha num `<Paper variant="outlined">` próprio. Dentro
        do casulo isso desenharia uma segunda superfície sobre a primeira — o mesmo
        defeito que o casulo existe para eliminar. Aqui ela é achatada, e só aqui.
      */}
      <Box
        sx={{
          [`& > .${paperClasses.root}`]: {
            border: 0,
            borderRadius: 0,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        }}
      >
        {children}
      </Box>

      {pager}
    </Card>
  );
}

// ----------------------------------------------------------------------
// Coluna de ações — DECISOES-PENDENTES.md §3.3 (9 lotes)
// ----------------------------------------------------------------------

export type DsAcaoLinha<T extends GridValidRowModel> = {
  icon: DsIconName;
  /**
   * Nome acessível do botão, no imperativo e com o registro: "Excluir bairro".
   * O artboard desenha um `svg` nu, sem nome nenhum; aqui ele é obrigatório.
   */
  label: string | ((row: T) => string);
  onClick: (row: T, evento: MouseEvent<HTMLElement>) => void;
  disabled?: boolean | ((row: T) => boolean);
  /** Esconde a ação nesta linha. O espaço NÃO é reservado — o grupo é à direita. */
  hidden?: (row: T) => boolean;
  /** `danger` leva a `error.main` no hover e no foco. Padrão: `neutral`. */
  tone?: 'neutral' | 'danger';
};

export type ColunaAcoesOpcoes = {
  /** Padrão: 96 — a faixa do artboard. A grade aninhada do canvas usa 56 (72 na compacta). */
  width?: number;
  /**
   * `compacta` encolhe o botão de 32 para 24 e o glifo de 20 para 18, e a coluna
   * de 96 para 72.
   *
   * Não é enfeite: numa linha de `DS_ROW_HEIGHT_COMPACTA` (34) com os 6+6 de
   * respiro da célula sobram 22px de caixa, e o botão de 32 transbordaria. Quem
   * usa `SX_GRADE_CANVAS_COMPACTA` e tem coluna de ações PRECISA passar isto.
   *
   * 24px é o piso de alvo de clique que este kit aceita em grade densa de desktop
   * — abaixo disso o botão fica difícil de acertar e a compactação passa do ponto.
   */
  densidade?: DsDensidadeGrade;
  /** Padrão: `'acoes'`. Troque se a linha já tiver um campo com esse nome. */
  field?: string;
  /** Padrão: `''` — o artboard não rotula esta coluna. */
  headerName?: string;
};

/**
 * `.iconbtn` da linha: caixa de 30px em volta de um glifo de 18px.
 *
 * Declarado como `SystemStyleObject<Theme>`, e não `SxProps<Theme>`, porque só
 * assim ele entra num array de `sx` sem `as` — é o mesmo tropeço de §3.9.
 */
function sxAcao(compacta: boolean): SystemStyleObject<Theme> {
  return {
    // 32×32 com glifo de 20px. O canvas mede `.iconbtn svg{width:15px}`, que na
    // linha mais densa ficou pequeno demais e desproporcional ao texto de 13px —
    // o dono do produto pediu o aumento olhando a tela. Divergência consciente.
    //
    // Na densidade compacta volta a 24×24: é o que cabe na linha de 34px, e ainda
    // é alvo de clique aceitável em grade de desktop.
    width: compacta ? 24 : 32,
    height: compacta ? 24 : 32,
    flexShrink: 0,
    display: 'inline-grid',
    placeItems: 'center',
    borderRadius: `${DS_BOX_RADIUS}px`,
    color: 'text.secondary',
    '&.Mui-disabled': { color: 'text.disabled' },
  };
}

/**
 * Última coluna do `.trow`: 96px, conteúdo colado à direita, `gap:4px`, ícones de
 * traço de 18px em `text.secondary`.
 *
 * Nove lotes escreveram isto à mão porque a coluna automática do `formatColumns`
 * (`tx-data-grid/utils/formatColumns.tsx`, usada por 70+ grades) monta um menu de
 * três pontos com `solar:eye-bold` / `solar:pen-bold` / `solar:trash-bin-trash-bold`
 * — ícone SÓLIDO, contra §3.4, e escondido atrás de um clique, contra o artboard.
 * O `formatColumns` só injeta a coluna dele quando a tela não declara nenhuma
 * coluna de ação manual, então basta declarar esta e **não** passar
 * `onView`/`onEdit`/`onDelete` ao `TxDataGrid`.
 *
 * O clique tem `stopPropagation`: em grade com linha clicável, o botão é o dono do
 * gesto — mesma regra do `colunaLink`.
 *
 * ```tsx
 * const cols = [
 *   colunaLink({ field: 'nome', headerName: 'Nome', flex: 1 }, abrir),
 *   colunaAcoes<Linha>([
 *     { icon: 'editar', label: 'Editar bairro', onClick: (row) => editar(row) },
 *     { icon: 'excluir', label: 'Excluir bairro', onClick: (row) => excluir(row), tone: 'danger' },
 *   ]),
 * ];
 * ```
 */
export function colunaAcoes<T extends GridValidRowModel>(
  acoes: DsAcaoLinha<T>[],
  opcoes: ColunaAcoesOpcoes = {}
): GridColDef<T> {
  const { densidade = 'padrao', field = 'acoes', headerName = 'Ações' } = opcoes;
  const compacta = densidade === 'compacta';
  const { width = compacta ? 72 : 96 } = opcoes;
  const sxBotao = sxAcao(compacta);

  return {
    field,
    headerName,
    width,
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    disableExport: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<T>) => {
      const visiveis = acoes.filter((acao) => !acao.hidden?.(params.row));

      if (visiveis.length === 0) {
        return null;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '4px',
            width: '100%',
          }}
        >
          {visiveis.map((acao) => {
            const rotulo = typeof acao.label === 'function' ? acao.label(params.row) : acao.label;
            const desabilitado =
              typeof acao.disabled === 'function' ? acao.disabled(params.row) : acao.disabled;

            return (
              <ButtonBase
                key={`${acao.icon}-${rotulo}`}
                aria-label={rotulo}
                title={rotulo}
                disabled={desabilitado}
                onClick={(evento: MouseEvent<HTMLElement>) => {
                  // A grade também escuta o clique na linha; o botão é o dono do gesto.
                  evento.stopPropagation();
                  acao.onClick(params.row, evento);
                }}
                sx={[
                  sxBotao,
                  acao.tone === 'danger' && {
                    '&:hover, &:focus-visible': { color: 'error.main' },
                  },
                ]}
              >
                <DsIcon name={acao.icon} size={compacta ? 18 : 20} />
              </ButtonBase>
            );
          })}
        </Box>
      );
    },
  };
}
